import h from 'hyperscript';

import type { AlertType } from 'ui/alert/alert';
import { ProgressSpinner } from 'ui/progress-spinner/progress-spinner';
import type { SelectItem, SelectOptionGroup } from 'ui/select/select';

import type { ProcessedResult } from 'select2';

// We use `any` because of select2's require function definition. See `Select2Require` select2/index.d.ts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type select2RequireFunction = (name: string) => any;

/*
 We use this singleton pattern because jest does not handle the AMD system well, and we would get TypeErrors.
 */
let DefaultResultAdapter: unknown;
export function getDefaultResultAdapter (select2Require: select2RequireFunction, resultsLimit: number) {
  if (!DefaultResultAdapter) {
    DefaultResultAdapter = createDefaultResultsAdapter(select2Require, resultsLimit);
  }
  return DefaultResultAdapter;
}

/*
 This custom results adapter applies the following changes to the default Select2 result adapter:
 - Loading indicator: Show a progress spinner instead of a text message
 - Messages (such as "no result found"): formats the message using our alert component
 See spec in : https://zpl.io/aXRPvYK,
 It was discussed with UX that these changes to the select appearance should be made standard.

 The adapter extends the default Select2 adapter, but since it is loaded dynamically via select2's
 AMD modules system, the class (or really the constructor) is accessed through the
 getDefaultResultAdapter() function, and is created lazily in this function and subsequently stored in a const.
 */
function createDefaultResultsAdapter (select2Require: select2RequireFunction, resultsLimit: number) {
  let Select2Utils = select2Require('select2/utils');
  let Results = select2Require('select2/results');
  Results = Select2Utils.Decorate(Results, select2Require('select2/dropdown/infiniteScroll'));
  Results = Select2Utils.Decorate(Results, select2Require('select2/dropdown/selectOnClose'));

  /*
  This class-based approach is more readable than the constructor/prototype based approach documented
  in the Select2 website and other tutorials, and works better with TS.
  In effect, it will create a new constructor and will not modify the prototype of the base Results
  adapter, which is what we want.
   */
  class CustomResultsClass extends Results {

    // This method is used by Select2; it overrides methods of the Results und InfiniteScroll adapters
    append (data: ProcessedResult<SelectItem>) {
      this.#disableInfiniteScroll();

      this.hideLoading();

      const $options = [];

      if (data.results == null || data.results.length === 0) {
        if (this.$results.children().length === 0) {
          this.trigger('results:message', {
            message: 'noResults'
          });
        }

        return;
      }

      data.results = this.sort(data.results);

      let currentResultsLength = this.$results.find('.select2-results__option--selectable').length;
      let hasMoreResults = false;

      for (const item of data.results) {
        if ('children' in item) {
          if (willOptionWithChildrenExceedLimit(item)) {
            hasMoreResults = true;
            item.children = item.children.slice(0, resultsLimit - currentResultsLength);
            $options.push(this.option(item));

            break;
          }
          $options.push(this.option(item));
          currentResultsLength += item.children.length;
        } else {
          if (currentResultsLength + 1 > resultsLimit) {
            hasMoreResults = true;
            break;
          }
          $options.push(this.option(item));
          currentResultsLength++;
        }
      }

      this.$results.append($options);

      if (hasMoreResults || areFurtherResultsAvailable(this.$results)) {
        this.trigger('results:message', {
          message: 'tooManyOptions',
          args: {
            type: 'info'
          }
        });
      } else {
        this.#enableInfiniteScroll(data);
      }

      function willOptionWithChildrenExceedLimit (item: SelectOptionGroup) {
        return item.children.length + currentResultsLength > resultsLimit;
      }

      function areFurtherResultsAvailable (resultsContainer: JQuery<Node>) {
        return resultsContainer.find('.select2-results__option--selectable').length === resultsLimit && data.pagination?.more;
      }
    }

    #disableInfiniteScroll () {
      this.$loadingMore.remove();
      this.loading = false;
    }

    #enableInfiniteScroll (data: ProcessedResult<SelectItem>) {
      if (this.showLoadingMore(data)) {
        this.$results.append(this.$loadingMore);
        this.loadMoreIfNeeded();
      }
    }

    // This method is used by Select2; it overrides `Results.prototype.showLoading`
    showLoading () {
      this.hideLoading();
      const resultElement = this.$results[0];
      resultElement.innerHTML = '';
      resultElement.append(
        h(
          '.loading-results.d-select--loading-spinner',
          new ProgressSpinner({ size: 's' })
        )
      );
    }

    // This method is used by Select2; it overrides `Results.prototype.displayMessage`
    displayMessage (params: { message: unknown; args?: { type?: AlertType } }) {
      this.hideLoading();

      const resultElement = this.$results[0];
      const message = this.options.get('translations').get(params.message);

      const alert = h(
        'd-alert',
        { size: 's', type: params.args?.type || 'info' },
        message(params.args));

      resultElement.append(alert);
    }

    // This method is used by Select2; it overrides `Results.prototype.hideMessages`
    hideMessages () {
      this.$results.find('.d-alert').remove();
    }

  }

  return CustomResultsClass;
}
